import { useCallback, useEffect, useState } from 'react';
import type { CycleRing } from '@/lib/cycleTypes';
import {
  createSpaceWeatherCycles,
  parseNoaaAlerts,
  type NoaaSpaceWeatherData,
} from '@/lib/spaceWeatherCycle';
import { buildAppsationsFunctionUrl } from '@/lib/appsationsSdkEndpoints';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';

// Do not import db at module scope to avoid circular import issues
const getDb = async () => {
  const { db } = await import('@/lib/firebase');
  return db;
};

/**
 * Hook to fetch and manage space weather cycle data
 * Subscribes to latest NOAA alerts and converts to cycle rings
 */
export function useSpaceWeatherCycles() {
  const [cycles, setCycles] = useState<CycleRing[]>([]);
  const [data, setData] = useState<NoaaSpaceWeatherData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      try {
        const db = await getDb();
        if (!db) {
          setError('Firestore not configured');
          setLoading(false);
          return;
        }

        const baseRef = collection(db, 'spaceWeatherReports');
        unsubscribe = onSnapshot(
          query(baseRef, orderBy('issue_datetime', 'desc'), limit(50)),
          (snap) => {
            if (cancelled) return;

            try {
              const alerts = snap.docs.map((doc) => doc.data());
              const parsedData = parseNoaaAlerts(alerts);
              setData(parsedData);
              setCycles(createSpaceWeatherCycles(parsedData));
              setLastUpdate(new Date());
              setError(null);
            } catch (e: any) {
              setError(`Failed to parse space weather data: ${e?.message || e}`);
            }

            setLoading(false);
          },
          (e: any) => {
            if (!cancelled) {
              setError(`Failed to fetch space weather cycles: ${e?.message || e}`);
              setLoading(false);
            }
          }
        );
      } catch (e: any) {
        if (!cancelled) {
          setError(`Space weather initialization failed: ${e?.message || e}`);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return {
    cycles,
    data,
    loading,
    error,
    lastUpdate,
  };
}

/**
 * Hook to fetch space weather data from NOAA via proxy
 * Used for initial population and standalone data fetching
 */
export function useSpaceWeatherData() {
  const [data, setData] = useState<NoaaSpaceWeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(buildAppsationsFunctionUrl('proxySpaceWeatherRss'));
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch space weather`);
      }

      const alerts = await response.json();
      const parsed = parseNoaaAlerts(alerts);
      setData(parsed);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    data,
    loading,
    error,
    fetchData,
  };
}
